import { useEffect, useMemo, useState } from "react";
import { Plug, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// Sprint P2-S0 — Connector Registry admin page.
//
// Route-guarded by `system_role === "platform_admin"` client-side; backend
// enforces `require_admin_dep` on all /api/admin/connectors* routes so a
// direct-curl bypass returns 403 (learning from the Sprint 2H auth defect).

const PHASE_FILTERS = [
  { key: "ALL",       label: "All"        },
  { key: "PHASE_2",   label: "Phase 2"    },
  { key: "ON_DEMAND", label: "On-demand"  },
  { key: "PHASE_3",   label: "Phase 3"    },
];

const AUTH_ICON = {
  OAUTH2:  { label: "OAuth 2.0", color: "#1e40af" },
  API_KEY: { label: "API key",   color: "#166534" },
  WEBHOOK: { label: "Webhook",   color: "#7c2d12" },
  NONE:    { label: "None",      color: "#475569" },
};

export default function AdminConnectors() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [pending, setPending] = useState(null); // connector selected for the events drawer
  const [events, setEvents] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    if (user && user.system_role !== "platform_admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/connectors");
      setPayload(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openEvents = async (c) => {
    setPending(c);
    setEvents(null);
    setLoadingEvents(true);
    try {
      const { data } = await api.get(`/admin/connectors/${c.connector_type}/events?limit=10`);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(apiError(e));
      setEvents([]);
    } finally { setLoadingEvents(false); }
  };

  const filtered = useMemo(() => {
    const rows = payload?.connectors || [];
    return filter === "ALL" ? rows : rows.filter((r) => r.phase === filter);
  }, [payload, filter]);

  const phaseCounts = useMemo(() => {
    const rows = payload?.connectors || [];
    return {
      ALL: rows.length,
      PHASE_2: rows.filter((r) => r.phase === "PHASE_2").length,
      ON_DEMAND: rows.filter((r) => r.phase === "ON_DEMAND").length,
      PHASE_3: rows.filter((r) => r.phase === "PHASE_3").length,
    };
  }, [payload]);

  if (user && user.system_role !== "platform_admin") return null;

  const stats = payload?.stats || {};

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="admin-connectors-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Plug size={22} style={{ color: "var(--blue)" }} />
            <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }} data-testid="admin-connectors-heading">
              Connector Registry
            </h1>
          </div>
        </div>

        <p className="text-sm mb-3" style={{ color: "var(--slate)" }}>
          The full list of external services this platform can integrate with.
          Phase 2 = live sprint scope. On-demand = wired only when a scheme
          activates them. Phase 3 = schema seeded, not yet wired.
        </p>

        {/* Stats header */}
        <div
          className="rounded-lg border px-4 py-3 mb-4 flex flex-wrap items-center gap-6 text-sm bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="admin-connectors-stats"
        >
          <StatChip value={stats.registered ?? 0} label="connectors registered" />
          <div className="h-8 w-px" style={{ background: "var(--border)" }} />
          <StatChip value={stats.active_integrations ?? 0} label="active integrations" />
          <div className="h-8 w-px" style={{ background: "var(--border)" }} />
          <StatChip value={stats.schemes_with_integrations ?? 0} label="schemes with integrations" />
        </div>

        {/* Phase filter chips */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-connectors-filter-chips">
          {PHASE_FILTERS.map((p) => (
            <button
              key={p.key}
              onClick={() => setFilter(p.key)}
              data-testid={`chip-${p.key}`}
              className="text-xs font-medium px-3 py-1.5 rounded-full border transition-all"
              style={{
                background: filter === p.key ? "var(--navy)" : "white",
                color:      filter === p.key ? "white" : "var(--slate)",
                borderColor: filter === p.key ? "var(--navy)" : "var(--border)",
              }}
            >
              {p.label} ({phaseCounts[p.key] ?? 0})
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: "var(--border)" }}>
          {!payload ? (
            <div className="p-5 space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: "var(--slate)" }} data-testid="admin-connectors-empty">
              No connectors in this phase.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead style={{ background: "#f8fafc" }}>
                <tr className="text-[11px] uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  <th className="text-left px-4 py-2 font-semibold">Connector</th>
                  <th className="text-left px-4 py-2 font-semibold">Auth</th>
                  <th className="text-left px-4 py-2 font-semibold">Phase</th>
                  <th className="text-left px-4 py-2 font-semibold">Active</th>
                  <th className="text-left px-4 py-2 font-semibold"># Schemes</th>
                  <th className="text-right px-4 py-2 font-semibold">Recent events</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.connector_type} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`connector-row-${c.connector_type}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold" style={{ color: "var(--navy)" }}>{c.display_name}</div>
                      <div className="text-[11px] font-mono" style={{ color: "var(--slate)" }}>{c.connector_type}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium" style={{ color: AUTH_ICON[c.auth_type]?.color || "#475569" }}>
                        {AUTH_ICON[c.auth_type]?.label || c.auth_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--slate)" }}>{c.phase.replace("_", " ")}</td>
                    <td className="px-4 py-3">
                      {c.is_active ? (
                        <CheckCircle2 size={14} style={{ color: "#166534" }} />
                      ) : (
                        <XCircle size={14} style={{ color: "#991b1b" }} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono" style={{ color: "var(--navy)" }}>
                      {c.connected_scheme_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEvents(c)}
                        data-testid={`view-events-${c.connector_type}`}
                      >
                        View last 10
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Events drawer */}
        <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
          <DialogContent className="max-w-2xl" data-testid="admin-connectors-events-modal">
            <DialogHeader>
              <DialogTitle style={{ color: "var(--navy)" }}>
                {pending?.display_name} — recent events
              </DialogTitle>
              <DialogDescription className="text-xs">
                Last 10 rows from <code>connector_events</code> filtered by
                connector_type = <code>{pending?.connector_type}</code>.
              </DialogDescription>
            </DialogHeader>

            {loadingEvents ? (
              <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
            ) : !events || events.length === 0 ? (
              <div
                className="rounded-md p-4 text-xs flex items-start gap-2"
                style={{ background: "#f1f5f9", color: "var(--slate)" }}
              >
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>No events recorded for this connector yet.</span>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-2 text-xs">
                {events.map((ev) => (
                  <div
                    key={ev.event_id}
                    className="rounded border p-2"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`event-row-${ev.event_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-semibold" style={{ color: "var(--navy)" }}>
                        {ev.event_type}
                      </span>
                      <span style={{ color: ev.status === "SUCCESS" ? "#166534" : ev.status === "FAILED" ? "#991b1b" : "var(--slate)" }}>
                        {ev.status === "SUCCESS" && <CheckCircle2 size={12} className="inline mr-1" />}
                        {ev.status === "FAILED" && <XCircle size={12} className="inline mr-1" />}
                        {ev.status === "PENDING" && <Clock size={12} className="inline mr-1" />}
                        {ev.status}
                      </span>
                    </div>
                    <div className="mt-1" style={{ color: "var(--slate)" }}>
                      scheme: <code>{ev.scheme_id || "—"}</code> · at {ev.created_at?.slice(0, 19)}
                    </div>
                    {ev.error && <div className="mt-1" style={{ color: "#991b1b" }}>error: {ev.error}</div>}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

function StatChip({ value, label }) {
  return (
    <div>
      <span className="text-2xl font-bold" style={{ color: "var(--navy)" }}>{value}</span>
      <span className="ml-1.5 text-xs uppercase tracking-wider" style={{ color: "var(--slate)" }}>{label}</span>
    </div>
  );
}
